import React, { useMemo, useContext } from 'react';
import { getLocale } from 'umi';
import { formatSmallValue, getChartDateFormatString } from '@/pages/dashboard/utils/utils';
import { yAxisFormat } from '@/pages/dashboard/components/Chart/_config/utils';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import Revenue from './Revenue';
import { barFormatterPcRevenueFunc, usePCRevenueColumns } from '../pcConsoleRevenue/components/PcRevenue';
import { isArray, isEmpty } from 'lodash-es';

function RevenueMetricsTrends(props: any) {
	const contextObj = useContext(FilterContext);
	const { data: defaultData = [], enName, cnName, defaultActive = 1, config: configs } = props;
	const { filterVal: formValue } = contextObj || {};
	const { data: dataSource, loading, errorMsg } = defaultData?.MonetizationRevenueData || {};
	const {
		chart: { type, decimal },
		componentMetricsConfig
	} = configs;
	const data = dataSource?.revenue_metrics_trends;
	const { timeType = 'Day' } = formValue;
	const revenueData = useMemo(() => {
		if (!isArray(data) || isEmpty(data)) return [];
		const dataSource = data?.map(item => {
			const { arpu, arppu } = item;
			return {
				date: item.date,
				system: '',
				metrics: {
					arpu: {
						value: arpu.today,
						dod: '',
						sdlw: '',
						change: ''
					},
					arppu: {
						value: arppu.today,
						dod: '',
						sdlw: '',
						change: ''
					}
				}
			};
		});
		return dataSource;
	}, [data]);
	const { tableOption, chart, tableData, downloadData } = usePCRevenueColumns(revenueData, componentMetricsConfig, timeType);
	const config = useMemo(() => {
		return {
			bar: {
				formatterFunc: (params: any[]) => barFormatterPcRevenueFunc(params, { type, decimal }),
				setOtherConfigFunc: (opt: {
					xAxis: { axisLabel: { formatter: (value: any) => any } }[];
					series: any[];
					yAxis: (
						| {
								type: string;
								position: string;
								splitLine: { show: boolean; lineStyle: { type: string; color: string } };
								axisLabel: { color: string; fontSize: number; formatter: (value: any) => any };
								alignTicks?: undefined;
						  }
						| {
								type: string;
								position: string;
								alignTicks: boolean; // 多Y轴时，开启是否对齐刻度线
								splitLine: { show: boolean; lineStyle: { type: string; color: string } };
								axisLabel: { formatter: (value: any) => string | number; color?: undefined; fontSize?: undefined };
						  }
					)[];
				}) => {
					opt.xAxis[0].axisLabel.formatter = (value: any) => getChartDateFormatString(timeType, value);
					opt.series = opt.series?.map((item: any, index: any) => {
						return {
							...item,
							yAxisIndex: index
						};
					});
					opt.yAxis = [
						{
							type: 'value',
							position: 'left',
							splitLine: {
								show: true,
								lineStyle: {
									type: 'dashed',
									color: '#ddd'
								}
							},
							axisLabel: {
								color: '#888888',
								fontSize: 12,
								formatter: (value: any) => formatSmallValue(value, type, decimal)
							}
						},
						{
							type: 'value',
							position: 'right',
							alignTicks: true, // 多Y轴时，开启是否对齐刻度线
							splitLine: {
								show: true,
								lineStyle: {
									type: 'dashed',
									color: '#ddd'
								}
							},
							axisLabel: {
								formatter: (value: number) => yAxisFormat(value)
							}
						}
					];
					return opt;
				}
			},
			line: {
				formatterFunc: (params: any[]) => barFormatterPcRevenueFunc(params, { type, decimal }),
				setOtherConfigFunc: (opt: {
					xAxis: { axisLabel: { formatter: (value: any) => any } }[];
					series: any[];
					yAxis: (
						| {
								type: string;
								position: string;
								splitLine: { show: boolean; lineStyle: { type: string; color: string } };
								axisLabel: { color: string; fontSize: number; formatter: (value: any) => any };
								alignTicks?: undefined;
						  }
						| {
								type: string;
								position: string;
								alignTicks: boolean; // 多Y轴时，开启是否对齐刻度线
								splitLine: { show: boolean; lineStyle: { type: string; color: string } };
								axisLabel: { formatter: (value: any) => string | number; color?: undefined; fontSize?: undefined };
						  }
					)[];
				}) => {
					opt.xAxis[0].axisLabel.formatter = (value: any) => getChartDateFormatString(timeType, value);
					opt.series = opt.series?.map((item: any, index: any) => {
						return {
							...item,
							stack: '',
							showSymbol: true,
							yAxisIndex: index
						};
					});
					opt.yAxis = [
						{
							type: 'value',
							position: 'left',
							splitLine: {
								show: true,
								lineStyle: {
									type: 'dashed',
									color: '#ddd'
								}
							},
							axisLabel: {
								color: '#888888',
								fontSize: 12,
								formatter: (value: any) => formatSmallValue(value, type, decimal)
							}
						},
						{
							type: 'value',
							position: 'right',
							alignTicks: true, // 多Y轴时，开启是否对齐刻度线
							splitLine: {
								show: true,
								lineStyle: {
									type: 'dashed',
									color: '#ddd'
								}
							},
							axisLabel: {
								formatter: (value: number) => yAxisFormat(value)
							}
						}
					];
					return opt;
				}
			}
		};
	}, [revenueData, getLocale()]);
	const options = useMemo(() => {
		return {
			chart,
			table: {
				option: tableOption,
				data: tableData,
				downloadData
			}
		};
	}, [revenueData, getLocale()]);

	return (
		<Revenue
			{...{
				otherOption: options,
				data: {
					MonetizationRevenueData: {
						data: {
							revenue: revenueData
						},
						loading,
						errorMsg
					}
				},
				enName,
				cnName,
				renderFormatterFunc: (params: any) => {
					const result = formatSmallValue(params.value, type, decimal);
					return result;
				},
				otherConfig: config,
				defaultActive,
				isShowLegend: true
			}}
		/>
	);
}

export default RevenueMetricsTrends;
